import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Chat-log scroll pinning for a streaming transcript region: follow the
 * newest text as it arrives, release the pin while the user scrolls up to
 * review, re-engage when they return to the bottom. Extracted from
 * QuickVoiceComposer's live-transcript region.
 */
export function useTranscriptPin(text: string): {
  ref: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  /** True while the user has scrolled away from the top edge (drives a fade mask). */
  scrolled: boolean;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const [scrolled, setScrolled] = useState(false);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setScrolled(el.scrollTop > 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
    setScrolled(el.scrollTop > 4);
  }, [text]);

  return { ref, onScroll, scrolled };
}
